import React, { useState, useEffect, useRef, Suspense } from "react"
import PropTypes from "prop-types"
import Modal from "@kit/design/modal"
import Stack from "@kit/design/stack"
import Note from "@kit/design/note"
import Icon from "@kit/design/icon"
import Loading from "./loading"

import { ErrorBoundary } from "react-error-boundary"
import { useSelectFile } from "sdk/filekit/mutations"
import useFeature, { useStaticFeature } from "hooks/use-feature"
import { useMediaGalleryPlugins } from "sdk/plugins/queries"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import { sendPluginEvent } from "sdk/plugins/plugin-event"

const FilekitUploader = React.lazy(() => import("./filekit-uploader"))
const FavoritesPicker = React.lazy(() => import("./favorites-picker"))
const RecentlyUsedPicker = React.lazy(() => import("./recently-used-picker"))
const UnsplashPicker = React.lazy(() => import("./unsplash-picker"))
const InstagramPicker = React.lazy(() => import("./instagram-picker"))
const PatternPicker = React.lazy(() => import("./pattern-picker"))
const PluginPicker = React.lazy(() => import("./plugin-picker"))

import unsplashLogo from "./unsplash.png"
import instagramLogo from "./instagram-filled.png"
import heroPatternsLogo from "./hero-patterns.png"

import SourceTab from "./source-tab"
import PluginTabs from "./plugin-tabs"
import GalleryPickerSelect from "./components/gallery-picker-select"

const INTEGRATIONS = {
  filekit: FilekitUploader,
  favorites: FavoritesPicker,
  recentlyUsed: RecentlyUsedPicker,
  unsplash: UnsplashPicker,
  instagram: InstagramPicker,
  heroPatterns: PatternPicker
}

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="flex-auto">
      <Note
        type="error"
        title="Something went wrong"
        message={error.message}
        action={{
          label: "Try again",
          onClick: resetErrorBoundary
        }}
      />
    </div>
  )
}

const useMediaGalleryPluginFeature = () => {
  // If the plugin feature is enabled staticically, we can return that immediately
  const isMediaGalleryPluginEnabled = useStaticFeature("media_gallery_plugin") // eslint-disable-line react-hooks/rules-of-hooks
    .enabled

  // If the plugin is not enabled in the static feature flag, we need fetch the feature
  if (!isMediaGalleryPluginEnabled) {
    return useFeature("media_gallery_plugin").enabled // eslint-disable-line react-hooks/rules-of-hooks
  }

  return isMediaGalleryPluginEnabled
}
const FileGallery = ({
  enablePatterns,
  onClose,
  allowedFileTypes,
  openedBy,
  disabledIntegrations = [],
  preloadedPlugins,
  initialActiveTab = "filekit"
}) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab)

  const isMediaGalleryPluginEnabled = useMediaGalleryPluginFeature()

  const {
    data: plugins,
    isError: errorFetchingPlugins,
    isLoading: isLoadingPlugins
  } = useMediaGalleryPlugins({
    enabled: isMediaGalleryPluginEnabled && !preloadedPlugins,
    staleTime: 1000 * 60 * 10, // 10 minutes of cache
    suspense: false,
    initialData: preloadedPlugins && { data: preloadedPlugins }
  })

  // Get the appropriate component based on whether it's a built-in tab or plugin
  const Uploader =
    INTEGRATIONS[activeTab] ||
    (plugins?.data?.find(p => p.id === activeTab)
      ? PluginPicker
      : FilekitUploader) // We should never fall back to FilekitUploader, but better safe than sorry

  const currentTabHandle = useRef()
  const selectFile = useSelectFile()
  const md = useMediaBreakpoint("md")

  const handleClose = file => {
    // A tab can prevent the modal from closing
    if (
      currentTabHandle.current &&
      !currentTabHandle.current.beforeClose(file)
    ) {
      return
    }

    if (file?.id) {
      selectFile.mutate(file.id)

      trackPluginAddedEvent(file)
    }
    onClose(file)
  }

  // Prevent image elements from accessing pattern backgrounds.
  useEffect(() => {
    if (!enablePatterns && activeTab === "heroPatterns") setActiveTab("filekit")
  }, [enablePatterns, activeTab])

  const hideIntegrationsMenu =
    disabledIntegrations.includes("unsplash") &&
    disabledIntegrations.includes("instagram") &&
    disabledIntegrations.includes("heroPatterns")

  return (
    <Modal
      label="Media Gallery"
      size="new-full"
      onClose={handleClose}
      separator
    >
      <div className="flex flex-col md:flex-row gap-2 md:gap-6 h-full overflow-hidden">
        {md ? (
          /* Desktop sidebar navigation */
          <Stack
            spacing={2}
            className="flex-none md:w-64 sticky"
            style={{ top: 24 }}
          >
            <SourceTab
              icon={<Icon name="cloud-upload" />}
              title="Uploads"
              active={activeTab === "filekit"}
              onClick={() => setActiveTab("filekit")}
            ></SourceTab>
            <SourceTab
              icon={<Icon name="heart" />}
              title="Favorites"
              active={activeTab === "favorites"}
              onClick={() => setActiveTab("favorites")}
            ></SourceTab>
            <SourceTab
              icon={<Icon name="refresh" />}
              title="Recently used"
              active={activeTab === "recentlyUsed"}
              onClick={() => setActiveTab("recentlyUsed")}
            ></SourceTab>
            {!hideIntegrationsMenu && (
              <React.Fragment>
                {!disabledIntegrations.includes("unsplash") && (
                  <SourceTab
                    icon={
                      <img
                        src={unsplashLogo}
                        className="border border-gray-200 p-1 rounded-sm"
                        alt=""
                      />
                    }
                    title="Unsplash"
                    subtitle="Beautiful, free images"
                    active={activeTab == "unsplash"}
                    onClick={() => setActiveTab("unsplash")}
                  />
                )}
                {!disabledIntegrations.includes("instagram") && (
                  <SourceTab
                    icon={
                      <img
                        src={instagramLogo}
                        className="border border-gray-200 p-1 rounded-sm"
                        alt=""
                      />
                    }
                    title="Instagram"
                    subtitle="Photos from your profile"
                    active={activeTab == "instagram"}
                    onClick={() => setActiveTab("instagram")}
                  />
                )}
                {enablePatterns &&
                  !disabledIntegrations.includes("heroPatterns") && (
                    <SourceTab
                      icon={
                        <img
                          src={heroPatternsLogo}
                          className="border-2 border-gray-200 p-1 rounded-sm"
                          alt=""
                        />
                      }
                      title="Hero Patterns"
                      subtitle="Free seamless patterns"
                      active={activeTab == "heroPatterns"}
                      onClick={() => setActiveTab("heroPatterns")}
                    />
                  )}
              </React.Fragment>
            )}
            {isMediaGalleryPluginEnabled && (
              <PluginTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                hasError={errorFetchingPlugins}
                isLoading={isLoadingPlugins}
                plugins={plugins?.data}
              />
            )}
          </Stack>
        ) : (
          /* Mobile dropdown selector */
          <GalleryPickerSelect
            activeTab={activeTab}
            onTabChange={tab => setActiveTab(tab)}
            plugins={plugins?.data}
            disabledIntegrations={disabledIntegrations}
            enablePatterns={enablePatterns}
            isMediaGalleryPluginEnabled={isMediaGalleryPluginEnabled}
          />
        )}

        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {Uploader && (
            <Suspense fallback={<Loading />}>
              <div className="flex-1 overflow-y-auto">
                <Uploader
                  key={activeTab}
                  ref={currentTabHandle}
                  allowedFileTypes={allowedFileTypes}
                  onClose={handleClose}
                  openedBy={openedBy}
                  plugin={plugins?.data?.find(p => p.id === activeTab)}
                />
              </div>
            </Suspense>
          )}
        </ErrorBoundary>
      </div>
    </Modal>
  )
}

function trackPluginAddedEvent(file) {
  const source = file.source
  const pluginId = source?.startsWith("plugin-")
    ? source.replace("plugin-", "")
    : null

  if (pluginId) {
    sendPluginEvent({ pluginId, action: "Added" })
  }
}

const FileGalleryWrapper = ({ active, ...rest }) => {
  if (!active) return null

  return <FileGallery {...rest} />
}

FileGalleryWrapper.propTypes = {
  active: PropTypes.bool,
  onClose: PropTypes.func,
  allowedFileTypes: PropTypes.array,
  enablePatterns: PropTypes.bool,
  openedBy: PropTypes.string,
  disabledIntegrations: PropTypes.array
}

export default FileGalleryWrapper
